const Resume = () => {
  return (
    <section className="px-4 py-20 sm:py-24">
      <div className="mx-auto max-w-6xl space-y-16 lg:space-y-20">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.4em] text-slate-400">
            Resume
          </p>
          <h2 className="mt-3 text-4xl font-bold text-white">
            Experience, education, and impact.
          </h2>
          <p className="mt-4 text-lg text-slate-300">
            Download the full résumé for details on internships, leadership, and
            the technologies I work with daily.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:gap-8">
          <article className="rounded-2xl border border-white/10 bg-white/5 p-8 text-slate-300">
            <h3 className="text-2xl font-semibold text-white">Snapshot</h3>
            <p className="mt-4 text-lg">
              Computer science student building AI-driven projects, developer
              tooling, and community resources. Comfortable working across the
              stack with Python, React, and data pipelines.
            </p>
            <ul className="mt-6 space-y-3 text-base">
              <li className="flex items-start gap-2">
                <span className="text-brand">●</span>
                Development across AI, data, and education tooling
              </li>
              <li className="flex items-start gap-2">
                <span className="text-brand">●</span>
                Workshops, mentorship, and curriculum support for peers
              </li>
              <li className="flex items-start gap-2">
                <span className="text-brand">●</span>
                Hands-on deployment experience (Cloudflare, Vercel, GitHub Pages)
              </li>
            </ul>
          </article>

          <article className="rounded-2xl border border-white/10 bg-white/5 p-8 text-slate-300">
            <h3 className="text-2xl font-semibold text-white">Request a copy</h3>
            <p className="mt-4 text-lg">
              Prefer a PDF? Grab the latest version hosted on Google Drive or
              download the PDF directly.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                className="inline-flex items-center rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white shadow-glow transition hover:-translate-y-0.5"
                href="https://docs.google.com/document/d/11Av90GSfSIhnjiVHbFpdwCQtaNClwCZxsAUkQS6PUtU/edit?usp=sharing"
                target="_blank"
                rel="noreferrer"
              >
                Google Drive
              </a>
              <a
                className="inline-flex items-center rounded-full border border-white/20 px-6 py-3 text-sm font-semibold hover:border-brand"
                href="/Resume.pdf"
                download
              >
                Download PDF
              </a>
            </div>
          </article>
        </div>

        <div className="space-y-16">
          {/* Education */}
          <section>
            <h2 className="text-2xl font-semibold text-white sm:text-3xl">Education</h2>
            <div className="mt-6 space-y-8 text-base text-slate-300 sm:space-y-10 sm:relative sm:pl-4 sm:before:absolute sm:before:left-4 sm:before:top-0 sm:before:h-full sm:before:w-px sm:before:bg-white/10 sm:before:content-['']">
              <div className="sm:grid sm:grid-cols-[32px,220px,minmax(0,1fr)] sm:items-start sm:gap-6">
                <div className="relative hidden sm:block" aria-hidden="true">
                  <span className="absolute left-1/2 top-4 h-3 w-3 -translate-x-1/2 rounded-full border-2 border-slate-900 bg-brand shadow-glow" />
                </div>
                <div className="flex flex-col gap-1 text-sm text-slate-400">
                  <div className="hidden sm:flex sm:flex-col sm:items-start sm:gap-2">
                    <p className="text-base font-semibold text-white">Chapel Hill, NC</p>
                  </div>
                  <p className="hidden sm:block">Aug 2025 - May 2028 (expected)</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                  <p className="text-sm font-semibold text-white sm:hidden">Chapel Hill, NC</p>
                  <p className="mb-2 text-sm text-slate-400 sm:hidden">Aug 2025 - May 2028 (expected)</p>
                  <h3 className="text-base font-semibold text-white">
                    University of North Carolina at Chapel Hill
                  </h3>
                  <p className="mt-1 text-sm text-slate-400">
                    B.S. Computer Science {' • '} B.S. Data Science
                  </p>
                  <p className="mt-3 text-lg">
                    Relevant Coursework: Data Structures, Discrete Structures,
                    Intro to Data Science, Calculus, Statistics.
                  </p>
                </div>
              </div>

              <div className="sm:grid sm:grid-cols-[32px,220px,minmax(0,1fr)] sm:items-start sm:gap-6">
                <div className="relative hidden sm:block" aria-hidden="true">
                  <span className="absolute left-1/2 top-4 h-3 w-3 -translate-x-1/2 rounded-full border-2 border-slate-900 bg-brand shadow-glow" />
                </div>
                <div className="flex flex-col gap-1 text-sm text-slate-400">
                  <div className="hidden sm:flex sm:flex-col sm:items-start sm:gap-2">
                    <p className="text-base font-semibold text-white">Chelmsford, MA</p>
                  </div>
                  <p className="hidden sm:block">Sept 2021 - June 2025</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                  <p className="text-sm font-semibold text-white sm:hidden">Chelmsford, MA</p>
                  <p className="mb-2 text-sm text-slate-400 sm:hidden">Sept 2021 - June 2025</p>
                  <h3 className="text-base font-semibold text-white">
                    Chelmsford High School
                  </h3>
                  <p className="mt-1 text-sm text-slate-400">
                    Information Technology Pathway
                  </p>
                  <p className="mt-1 text-base text-slate-200">GPA: 4.2</p>
                  <p className="mt-3 text-lg">
                    AP Computer Science A, AP Computer Science Principles, AP
                    Calculus BC, AP Physics 1, AP Chemistry, AP Environmental
                    Science, AP Precalculus.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Experience */}
          <section>
            <h2 className="text-2xl font-semibold text-white sm:text-3xl">Experience</h2>
            <div className="mt-6 space-y-8 text-base text-slate-300 sm:space-y-10 sm:relative sm:pl-4 sm:before:absolute sm:before:left-4 sm:before:top-0 sm:before:h-full sm:before:w-px sm:before:bg-white/10 sm:before:content-['']">
              <div className="sm:grid sm:grid-cols-[32px,220px,minmax(0,1fr)] sm:items-start sm:gap-6">
                <div className="relative hidden sm:block" aria-hidden="true">
                  <span className="absolute left-1/2 top-4 h-3 w-3 -translate-x-1/2 rounded-full border-2 border-slate-900 bg-brand shadow-glow" />
                </div>
                <div className="flex flex-col gap-1 text-sm text-slate-400">
                  <div className="hidden sm:flex sm:flex-col sm:items-start sm:gap-2">
                    <p className="text-base font-semibold text-white">Chelmsford, MA</p>
                  </div>
                  <p className="hidden sm:block">Dec 2022 - Mar 2025</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                  <p className="text-sm font-semibold text-white sm:hidden">Chelmsford, MA</p>
                  <p className="mb-2 text-sm text-slate-400 sm:hidden">Dec 2022 - Mar 2025</p>
                  <h3 className="text-base font-semibold text-white">
                    Referee - Chelmsford Youth Basketball League
                  </h3>
                  <ul className="mt-3 space-y-3 text-lg leading-relaxed">
                    <li className="text-slate-200">
                      • Officiated youth basketball games while ensuring player safety
                      and enforcing rules consistently.
                    </li>
                    <li className="text-slate-200">
                      • Managed on-court conflict and communicated calls calmly to
                      coaches, players, and spectators.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Leadership & Involvement */}
          <section>
            <h2 className="text-2xl font-semibold text-white sm:text-3xl">
              Leadership & Involvement
            </h2>
            <div className="mt-6 space-y-8 text-base text-slate-300 sm:space-y-10 sm:relative sm:pl-4 sm:before:absolute sm:before:left-4 sm:before:top-0 sm:before:h-full sm:before:w-px sm:before:bg-white/10 sm:before:content-['']">
              <div className="sm:grid sm:grid-cols-[32px,220px,minmax(0,1fr)] sm:items-start sm:gap-6">
                <div className="relative hidden sm:block" aria-hidden="true">
                  <span className="absolute left-1/2 top-4 h-3 w-3 -translate-x-1/2 rounded-full border-2 border-slate-900 bg-brand shadow-glow" />
                </div>
                <div className="flex flex-col gap-1 text-sm text-slate-400">
                  <div className="hidden sm:flex sm:flex-col sm:items-start sm:gap-2">
                    <p className="text-base font-semibold text-white">Chapel Hill, NC</p>
                  </div>
                  <p className="hidden sm:block">2025 - Present</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                  <p className="text-sm font-semibold text-white sm:hidden">Chapel Hill, NC</p>
                  <p className="mb-2 text-sm text-slate-400 sm:hidden">2025 - Present</p>
                  <h3 className="text-base font-semibold text-white">Ram Robotics</h3>
                  <p className="mt-2 text-lg">
                    Design and build VEX robots; collaborate on game strategy and competition prep.
                  </p>
                </div>
              </div>

              <div className="sm:grid sm:grid-cols-[32px,220px,minmax(0,1fr)] sm:items-start sm:gap-6">
                <div className="relative hidden sm:block" aria-hidden="true">
                  <span className="absolute left-1/2 top-4 h-3 w-3 -translate-x-1/2 rounded-full border-2 border-slate-900 bg-brand shadow-glow" />
                </div>
                <div className="flex flex-col gap-1 text-sm text-slate-400">
                  <div className="hidden sm:flex sm:flex-col sm:items-start sm:gap-2">
                    <p className="text-base font-semibold text-white">Chapel Hill, NC</p>
                  </div>
                  <p className="hidden sm:block">2025 - Present</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                  <p className="text-sm font-semibold text-white sm:hidden">Chapel Hill, NC</p>
                  <p className="mb-2 text-sm text-slate-400 sm:hidden">2025 - Present</p>
                  <h3 className="text-base font-semibold text-white">
                    UNC Quantum Computing Club
                  </h3>
                  <p className="mt-2 text-lg">
                    Learn quantum algorithms and explore their applications in cryptography, simulation,
                    and optimization.
                  </p>
                </div>
              </div>

              <div className="sm:grid sm:grid-cols-[32px,220px,minmax(0,1fr)] sm:items-start sm:gap-6">
                <div className="relative hidden sm:block" aria-hidden="true">
                  <span className="absolute left-1/2 top-4 h-3 w-3 -translate-x-1/2 rounded-full border-2 border-slate-900 bg-brand shadow-glow" />
                </div>
                <div className="flex flex-col gap-1 text-sm text-slate-400">
                  <div className="hidden sm:flex sm:flex-col sm:items-start sm:gap-2">
                    <p className="text-base font-semibold text-white">Chapel Hill, NC</p>
                  </div>
                  <p className="hidden sm:block">2025</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                  <p className="text-sm font-semibold text-white sm:hidden">Chapel Hill, NC</p>
                  <p className="mb-2 text-sm text-slate-400 sm:hidden">2025</p>
                  <h3 className="text-base font-semibold text-white">HackNC - NoBS Team</h3>
                  <p className="mt-2 text-lg">
                    Built a debate-tracking web app using Gemini, ElevenLabs, and Speechmatics; managed
                    collaboration with branches and pull requests on GitHub.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Skills */}
          <section>
            <h2 className="text-2xl font-semibold text-white sm:text-3xl">Skills</h2>
            <div className="mt-4 grid gap-4 text-base text-slate-300 md:grid-cols-2">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <h3 className="text-base font-semibold text-white">Programming Languages</h3>
                <p className="mt-2 text-lg">
                  Python, Java, JavaScript, C, C++, HTML, CSS, Swift
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <h3 className="text-base font-semibold text-white">Tools & Platforms</h3>
                <p className="mt-2 text-lg">
                  Visual Studio Code, XCode, IntelliJ, PyCharm, Git, GitHub, Git
                  Bash, DNS Management
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <h3 className="text-base font-semibold text-white">Frameworks</h3>
                <p className="mt-2 text-lg">
                  Flask, React, TensorFlow, Pygame, Mediapipe, Tkinter, SwiftUI
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <h3 className="text-base font-semibold text-white">Web & Media</h3>
                <p className="mt-2 text-lg">
                  Photoshop, Premiere Pro, After Effects, Microsoft Office Suite
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </section>
  );
};

export default Resume;
